import { type ResponseData, useApiQuery } from '@vrplatform/data';
import { type Maybe, sortBy, toTitleCase, utc } from '@vrplatform/utils';
import { useSalesTaxLiabilityTableFilter } from './useSalesTaxLiabilityTableFilter';

export const STORYBOOK_SALES_TAX_LIABILITY_TABLE_QUERY_KEY =
  'sales-tax-liability-table';

export type SalesTaxLiabilityTableQuerySorting =
  | 'guestName'
  | 'checkIn'
  | 'checkOut'
  | 'taxableIncomeCentTotal'
  | 'collectableTaxesCentTotal';

type ApiRow = ResponseData<'get:/reports/sales-tax-liability'>['data'][0];

type Base = {
  id: string;
  name: string;
  taxableIncomeCentTotal: number;
  collectableTaxesCentTotal: number;
};

type Reservation = {
  guestName: string;
  checkIn: Maybe<string>;
  checkOut: Maybe<string>;
  cancelledAt: Maybe<string>;
  bookingPlatform: string;
  nights: number;
  bookedAt: Maybe<string>;
  confirmationCode: string;
  type: 'reservation';
};

type Listing = {
  type: 'listing';
};

type BookingChannel = {
  type: 'bookingChannel';
};

export type SalesTaxLiabilityTableQueryRow = Base &
  (Reservation | Listing | BookingChannel) & {
    subRows: SalesTaxLiabilityTableQueryRow[];
  };

const formatRow = (
  listing: ApiRow,
  sorting: SalesTaxLiabilityTableQuerySorting
): SalesTaxLiabilityTableQueryRow => {
  return {
    id: listing.id,
    collectableTaxesCentTotal: listing.collectableTaxesTotal,
    taxableIncomeCentTotal: listing.taxableIncomeTotal,
    name: toTitleCase(listing.name),
    type: 'listing',
    subRows: sortBy(
      listing.channels.map((channel) => ({
        id: `${listing.id}-${channel.name}`,
        name: channel.name,
        type: 'bookingChannel',
        collectableTaxesCentTotal: channel.collectableTaxesTotal,
        taxableIncomeCentTotal: channel.taxableIncomeTotal,
        subRows: sortBy(
          channel.reservations.map((reservation) => ({
            id: reservation.id,
            name: reservation.guestName,
            collectableTaxesCentTotal: reservation.collectableTaxes,
            taxableIncomeCentTotal: reservation.taxableIncome,
            guestName: reservation.guestName,
            bookingPlatform: channel.name,
            cancelledAt: null,

            // bookingPlatform: reservation.bookingPlatform,
            // cancelledAt: reservation.cancelledAt
            //   ? utc(reservation.cancelledAt).yyyymmdd()
            //   : null,
            checkIn: reservation.checkIn
              ? utc(reservation.checkIn).yyyymmdd()
              : null,
            checkOut: reservation.checkOut
              ? utc(reservation.checkOut).yyyymmdd()
              : null,
            nights: reservation.nights,
            bookedAt: reservation.bookedAt
              ? utc(reservation.bookedAt).yyyymmdd()
              : null,
            confirmationCode: reservation.confirmationCode,
            type: 'reservation',
            subRows: [],
          })),
          sorting,
          sorting === 'collectableTaxesCentTotal' ||
            sorting === 'taxableIncomeCentTotal'
            ? 'desc'
            : 'asc'
        ),
      })),
      'name'
    ),
  };
};

export const useSalesTaxLiabilityTableQuery = (
  sorting: SalesTaxLiabilityTableQuerySorting
) => {
  const { filter } = useSalesTaxLiabilityTableFilter();

  return useApiQuery(
    async ($api, signal, args) => {
      const result = await $api.GET('/reports/sales-tax-liability', {
        params: {
          query: {
            startDate: args.filter.startDate,
            endDate: args.filter.endDate,
            taxableLineIds: args.filter.taxableLineIds?.join(','),
            channels: args.filter.channels?.join(','),
          },
        },
        signal,
      });

      if (result.error) throw result.error;

      return {
        rows: sortBy(
          result.data.data.map((x) => formatRow(x, sorting)),
          'name'
        ),
        taxesIncomeTotal: result.data.taxesIncomeTotal,
        collectableTaxesTotal: result.data.collectableTaxesTotal,
      };
    },
    {
      storybookQueryKey: STORYBOOK_SALES_TAX_LIABILITY_TABLE_QUERY_KEY,
      queryGroup: 'reports',
      uniqueQueryKey: 'sales-tax-liability-report',
      queryKeys: ['journalEntries', 'reports'],
      skip: !filter.taxableLineIds.length || !filter.endDate,
      variables: {
        filter,
        sorting,
      },
    }
  );
};
